import type { MembershipRead, PersonRead } from '@pedigreesmart/api-client'
import {
  annoProposto,
  annoValidoA,
  etichettaStatoQuota,
  inRegola,
  statoQuota,
} from '@pedigreesmart/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Documenti } from '../components/Documenti.js'
import { useSession } from '../session/SessionProvider.js'
import { MembershipForm } from './MembershipForm.js'
import './people.css'

/**
 * La scheda di una persona, col suo tesseramento.
 *
 * I due dati stanno insieme perché la domanda che si fa in segreteria è una
 * sola — «questo socio è in regola?» — e richiede tutte e due le risposte.
 * Tenere i tesseramenti in una sezione separata costringerebbe a cercare due
 * volte la stessa persona.
 *
 * Lo stato in regola **non** si legge da un campo del server: si calcola dalle
 * date di validità e dai pagamenti (`inRegola` in `core`), perché non tutte le
 * associazioni usano l'anno solare e il numero dell'anno da solo mentirebbe a
 * chi apre la campagna a settembre.
 */
export function PersonDetail({
  id,
  onTorna,
  onModifica,
}: {
  id: number
  onTorna: () => void
  onModifica?: ((id: number) => void) | undefined
}) {
  const { resources } = useSession()
  const [apreTesseramento, setApreTesseramento] = useState(false)

  const persona = useQuery({
    queryKey: ['person', id],
    queryFn: ({ signal }) => resources.people.get(id, signal),
  })

  const tesseramenti = useQuery({
    queryKey: ['memberships', 'person', id],
    queryFn: ({ signal }) => resources.memberships.list({ 'person.id': id }, signal),
  })

  return (
    <section aria-labelledby="titolo-persona">
      <button type="button" data-variante="piatto" onClick={onTorna}>
        ← Torna all'elenco
      </button>

      {persona.isPending && <p className="messaggio">Caricamento…</p>}

      {persona.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere la scheda.
        </p>
      )}

      {persona.data && (
        <>
          <div className="elenco__testa">
            <h1 id="titolo-persona">{persona.data.display_name}</h1>
            <div className="elenco__azioni">
              {persona.data.breeder && <span className="stato">Allevatore</span>}
              {onModifica && (
                <button
                  type="button"
                  data-variante="secondario"
                  onClick={() => {
                    onModifica(id)
                  }}
                >
                  Modifica
                </button>
              )}
            </div>
          </div>

          {persona.data.anonymized_at && (
            <p className="messaggio">
              I dati di contatto sono stati cancellati su richiesta dell'interessato. Il nome resta
              perché compare su certificati già consegnati ad altre persone.
            </p>
          )}

          <dl className="scheda">
            <Voce nome="Affisso" valore={persona.data.kennel_prefix} />
            <Voce nome="Codice fiscale" valore={persona.data.tax_code} codice />
            <Voce nome="Partita IVA" valore={persona.data.vat_number} codice />
            <Voce nome="Indirizzo" valore={indirizzo(persona.data)} />
            <Voce nome="Telefono" valore={persona.data.mobile ?? persona.data.phone} />
            <Voce nome="Posta elettronica" valore={persona.data.email} />
            <Voce nome="Qualifica" valore={persona.data.qualification} />
          </dl>

          {tesseramenti.isError && (
            <p role="alert" className="messaggio messaggio--errore">
              Non è stato possibile leggere i tesseramenti.
            </p>
          )}

          {tesseramenti.data?.member.length === 0 && !apreTesseramento && (
            <p className="messaggio">
              Questa persona non è mai stata tesserata.{' '}
              <button
                type="button"
                data-variante="piatto"
                onClick={() => {
                  setApreTesseramento(true)
                }}
              >
                Apri un tesseramento
              </button>
            </p>
          )}

          {apreTesseramento && (
            <MembershipForm
              personaId={id}
              onFatto={() => {
                setApreTesseramento(false)
              }}
              onAnnulla={() => {
                setApreTesseramento(false)
              }}
            />
          )}

          {tesseramenti.data?.member.map((tesseramento) => (
            <Tesseramento key={tesseramento.id} tesseramento={tesseramento} />
          ))}

          <Documenti ownerType="person" ownerId={id} />

          <DirittiInteressato persona={persona.data} />
        </>
      )}
    </section>
  )
}

/**
 * I due diritti che si esercitano da qui: accesso e cancellazione.
 *
 * Stanno in fondo alla scheda e non fra i pulsanti in alto. Non è timidezza:
 * la cancellazione è irreversibile e non deve trovarsi accanto a «Modifica»,
 * dove il dito arriva per abitudine.
 *
 * **La cancellazione non cancella tutto, e la conferma lo dice.** Il nome e
 * l'affisso restano, perché sono stampati su certificati già consegnati ad
 * altre persone: toglierli falsificherebbe documenti altrui. Chi conferma
 * deve saperlo prima, non scoprirlo dopo guardando la scheda.
 */
function DirittiInteressato({ persona }: { persona: PersonRead }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [conferma, setConferma] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const esporta = useMutation({
    mutationFn: () => resources.people.export(persona.id),
    onSuccess: (dati) => {
      // Il file si compone nel browser: l'esportazione è una risposta JSON
      // autenticata, e un semplice collegamento non porterebbe il token.
      const indirizzo = URL.createObjectURL(
        new Blob([JSON.stringify(dati, null, 2)], { type: 'application/json' }),
      )
      const collegamento = document.createElement('a')

      collegamento.href = indirizzo
      collegamento.download = `dati-persona-${String(persona.id)}.json`
      collegamento.click()
      URL.revokeObjectURL(indirizzo)
    },
    onError: () => {
      setErrore('Non è stato possibile preparare l\'esportazione.')
    },
  })

  const cancella = useMutation({
    mutationFn: () => resources.people.anonymize(persona.id),
    onSuccess: () => {
      setConferma(false)
      void cache.invalidateQueries({ queryKey: ['person', persona.id] })
      void cache.invalidateQueries({ queryKey: ['people'] })
    },
    onError: (e: unknown) => {
      setErrore(
        (e as { status?: number }).status === 409
          ? 'I dati di questa persona erano già stati cancellati.'
          : 'Non è stato possibile completare la cancellazione.',
      )
    },
  })

  return (
    <section className="diritti" aria-labelledby="titolo-diritti">
      <h2 id="titolo-diritti">Diritti dell'interessato</h2>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      <div className="diritti__azioni">
        <button
          type="button"
          data-variante="secondario"
          disabled={esporta.isPending}
          onClick={() => {
            setErrore(null)
            esporta.mutate()
          }}
        >
          {esporta.isPending ? 'Preparazione…' : 'Esporta i dati (art. 15)'}
        </button>

        {!persona.anonymized_at && !conferma && (
          <button
            type="button"
            data-variante="piatto"
            onClick={() => {
              setErrore(null)
              setConferma(true)
            }}
          >
            Cancella i dati (art. 17)
          </button>
        )}
      </div>

      {conferma && (
        <div className="conferma" role="group" aria-label="Conferma della cancellazione">
          <h3>Cosa verrà cancellato</h3>
          <ul>
            <li>Spariscono: recapiti, indirizzo, codice fiscale, documento, data di nascita.</li>
            <li>
              Restano: <strong>nome e affisso</strong>, perché compaiono su certificati già
              consegnati ad altre persone.
            </li>
            <li>L'operazione non si può annullare né ripetere.</li>
          </ul>
          <div className="diritti__azioni">
            <button
              type="button"
              data-variante="piatto"
              disabled={cancella.isPending}
              onClick={() => {
                setConferma(false)
              }}
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={cancella.isPending}
              onClick={() => {
                cancella.mutate()
              }}
            >
              {cancella.isPending ? 'Cancellazione…' : 'Cancella definitivamente'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function Tesseramento({ tesseramento }: { tesseramento: MembershipRead }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)

  const anni = tesseramento.years ?? []
  const oggi = new Date()
  const corrente = annoValidoA(anni, oggi)
  const regolare = inRegola(anni, oggi)

  const [anno, setAnno] = useState(() => annoProposto(anni, oggi))
  const [quota, setQuota] = useState('')

  const aggiungi = useMutation({
    mutationFn: () =>
      resources.memberships.addYear(
        tesseramento.id,
        anno,
        // Una casella vuota è «non lo so ancora», non «zero»: zero è una quota
        // condonata, e le due cose si mostrano diversamente.
        quota.trim() === '' ? undefined : Number(quota.replace(',', '.')),
      ),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      setQuota('')
      void cache.invalidateQueries({ queryKey: ['memberships'] })
    },
    onError: (e: unknown) => {
      const stato = (e as { status?: number }).status

      setErrore(
        stato === 422
          ? 'Quest\'anno risulta già registrato per questo tesseramento.'
          : 'Non è stato possibile aggiungere l\'anno.',
      )
    },
  })

  return (
    <div className="tesseramento">
      <div className="tesseramento__testa">
        <div>
          <span className="codice">{tesseramento.member_number}</span>
          <p className="riga__secondario">Iscritto dal {data(tesseramento.joined_on)}</p>
        </div>
        {/* La domanda a cui questa schermata deve rispondere in un colpo
            d'occhio. L'anno da solo non basta: aperto e non pagato non è in
            regola. */}
        <span className="stato" data-regola={regolare ? 'si' : 'no'}>
          {regolare
            ? `In regola per il ${String(corrente?.year ?? oggi.getFullYear())}`
            : 'Non in regola'}
        </span>
      </div>

      {tesseramento.left_on && (
        <p className="riga__secondario">Uscito il {data(tesseramento.left_on)}</p>
      )}

      {anni.length > 0 && (
        <ul className="anni">
          {[...anni]
            .sort((a, b) => b.year - a.year)
            .map((riga) => (
              <li key={riga.id} className="anno" data-quota={statoQuota(riga)}>
                <span className="anno__numero">{riga.year}</span>
                <span className="anno__quota">
                  {riga.fee === null || riga.fee === undefined
                    ? '—'
                    : riga.fee.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                </span>
                <span className="anno__stato">{etichettaStatoQuota(statoQuota(riga))}</span>
                {riga.paid_at && <span className="riga__secondario">{data(riga.paid_at)}</span>}
              </li>
            ))}
        </ul>
      )}

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      <form
        className="anno__aggiunta"
        onSubmit={(event) => {
          event.preventDefault()
          aggiungi.mutate()
        }}
      >
        <label>
          <span>Anno</span>
          <input
            type="number"
            value={anno}
            min={1900}
            max={2200}
            onChange={(event) => {
              setAnno(Number(event.target.value))
            }}
          />
        </label>
        <label>
          <span>Quota</span>
          <input
            type="text"
            inputMode="decimal"
            value={quota}
            placeholder="facoltativa"
            onChange={(event) => {
              setQuota(event.target.value)
            }}
          />
        </label>
        <button type="submit" data-variante="secondario" disabled={aggiungi.isPending}>
          {aggiungi.isPending ? 'Aggiunta…' : 'Aggiungi anno'}
        </button>
      </form>
    </div>
  )
}

function Voce({
  nome,
  valore,
  codice,
}: {
  nome: string
  valore: string | null | undefined
  codice?: boolean | undefined
}) {
  // Le voci vuote non si mostrano: una scheda piena di trattini è più
  // difficile da leggere di una corta.
  if (!valore) return null

  return (
    <>
      <dt>{nome}</dt>
      <dd className={codice ? 'codice' : undefined}>{valore}</dd>
    </>
  )
}

function indirizzo(persona: {
  address?: string | null
  postal_code?: string | null
  city?: string | null
  province?: string | null
}): string | null {
  const righe = [
    persona.address,
    [persona.postal_code, persona.city].filter(Boolean).join(' '),
    persona.province,
  ].filter((pezzo) => Boolean(pezzo?.trim()))

  return righe.length > 0 ? righe.join(' · ') : null
}

function data(iso?: string | null): string {
  if (!iso) return '—'

  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? '—' : quando.toLocaleDateString('it-IT')
}
